import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface DeliveryRow {
  readonly id: string;
  readonly channel: string;
  readonly recipient: string;
  readonly status: string;
  readonly attempts: number;
  readonly provider: string;
  readonly updatedAt: string;
}

const STATUSES = ['ALL', 'QUEUED', 'SENT', 'DELIVERED', 'READ', 'FAILED', 'BLOCKED', 'BOUNCED'] as const;

export function NotificationsDeliveryTrackingPage(): ReactNode {
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<DeliveryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('ALL');

  useEffect(() => {
    let alive = true;
    const qs = filter === 'ALL' ? '' : `?status=${filter}`;
    setRows(null);
    apiRequest<{ items: DeliveryRow[] }>(`/notifications/deliveries${qs}`, { token })
      .then((r) => { if (alive) setRows(r.items); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, filter]);

  if (error) return <p role="alert">{error}</p>;
  return (
    <main aria-label="delivery-tracking">
      <h1>{t('notif.delivery.title')}</h1>
      <label htmlFor="status-filter">{t('notif.common.status')}</label>
      <select id="status-filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {!rows ? <p>{t('notif.common.loading')}</p> : (
        <DataGrid<any>
          caption="deliveries-table"
          rows={rows}
          getRowId={(r) => r.id}
          columns={[
            { key: 'updated', header: t('notif.common.updated'), value: (r) => r.updatedAt, render: (r) => at(r.updatedAt) },
            { key: 'channel', header: t('notif.common.channel'), value: (r) => r.channel },
            { key: 'recipient', header: t('notif.common.recipient'), value: (r) => r.recipient },
            { key: 'provider', header: t('notif.common.provider'), value: (r) => r.provider },
            { key: 'attempts', header: t('notif.common.attempts'), value: (r) => r.attempts, numeric: true },
            { key: 'status', header: t('notif.common.status'), value: (r) => r.status },
          ]}
        />
      )}
    </main>
  );
}
